import { Link, useRouterState } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'motion/react';
import { useMemo } from 'react';

import { useEntitlements } from '@/lib/use-entitlements';
import {
  Radar,
  Activity,
  ShieldCheck,
  ShieldAlert,
  Fingerprint,
  Sparkles,
  LayoutDashboard,
  Search,
  GitBranch,
  Network,
  AlertTriangle,
  FileSearch,
  Activity as Stream,
  TrendingUp,
  Waves,
  Radio,
  Timer,
  Gavel,
  Workflow,
  ScrollText,
  KeyRound,
  Database,
  ListChecks,
  Grid3x3,
  Atom,
  Lock,
  Cpu,
  Brain,
  History,
  Zap,
  MessageSquare,
  Layers,
  ShieldOff,
  Lightbulb,
  Home,
  Settings,
  Plug,
  BadgeCheck,
  ScanLine,
  Users as UsersIcon,
  FileLock2,
  Building2,
  Hash,
  Award,
  Server,
  Edit3,
  Pause,
  Download,
  Megaphone,
  ClipboardCheck,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Mark } from '@/components/brand/Mark';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, ScrollArea, Separator } from '@/components/ui/primitives';
import { useEffect, useState } from 'react';
import { getBootstrapProgress } from '@/lib/api';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  /**
   * P1-9 + P1-12 (2026-05-26) - sub-section header rendered above the
   * item when this value differs from the previous item's `section`.
   * Lets a single flat array still render as visually-grouped without
   * changing the NavGroup type or the activeGroup state model.
   */
  section?: string;
}

/**
 * P1 OBS-3.alt (2026-05-26) - surface a dynamic "{N}/8" badge on the
 * 8-Level Bootstrap nav item so it actually reflects state instead
 * of the static "L1-L8" descriptor. Cache module-level so SideNav
 * re-renders (every nav transition) don't spam the bootstrap-progress
 * endpoint. One fetch per page load, refresh on app reload.
 */
let BOOTSTRAP_CACHE: { activeCount: number; ts: number } | null = null;
let BOOTSTRAP_INFLIGHT: Promise<void> | null = null;

function useBootstrapBadge(): string {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    // Skip if we've fetched in the last 5 minutes - covers the
    // common "user clicks around for a while" session pattern.
    if (BOOTSTRAP_CACHE && Date.now() - BOOTSTRAP_CACHE.ts < 5 * 60_000) return;
    if (BOOTSTRAP_INFLIGHT) {
      void BOOTSTRAP_INFLIGHT.then(() => setTick((t) => t + 1));
      return;
    }
    BOOTSTRAP_INFLIGHT = getBootstrapProgress()
      .then((p) => {
        BOOTSTRAP_CACHE = { activeCount: p.summary.completeCount ?? 0, ts: Date.now() };
      })
      .catch(() => {
        // Network failure → keep static fallback. Don't poison
        // the cache so we retry next page load.
      })
      .finally(() => {
        BOOTSTRAP_INFLIGHT = null;
        setTick((t) => t + 1);
      });
  }, []);
  // Re-render-driven by tick + we always read the latest cache value.
  void tick;
  return BOOTSTRAP_CACHE ? `${BOOTSTRAP_CACHE.activeCount}/8` : 'L1-L8';
}

interface NavGroup {
  id: 'discovery' | 'activity' | 'detection' | 'compliance' | 'frontier' | 'admin';
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  /**
   * 2026-05-27 - clicking the module-switcher pill should navigate to
   * that section's canonical landing page (Discovery → /discovery
   * dashboard, Detection → /detection operator dashboard, etc.), not
   * just toggle the active group. Falls back to the first item's `to`
   * when not specified.
   */
  defaultHref?: string;
  items: NavItem[];
  /**
   * Slice 2.4 - entitlement gate. Group renders only when the tenant's
   * entitlements include at least one of these modules. If null, group
   * always renders (e.g. onboarding + admin are universal).
   */
  requiresAnyModule?: ReadonlyArray<string>;
}

const NAV: NavGroup[] = [
  // 2026-05-27 - Welcome & Setup is no longer a top-level group.
  // The onboarding checklist still lives at /welcome (and the route
  // is registered in router.tsx) but is reached via Settings & Admin
  // → Onboarding checklist. The Discovery Dashboard at / (HomePage)
  // is the post-login landing page; brand mark + post-login flows
  // all target / now.
  {
    id: 'discovery',
    label: 'Discovery',
    icon: Radar,
    accent: 'from-chart-1 to-chart-2',
    defaultHref: '/discovery',
    requiresAnyModule: ['discovery'],
    // doc 33 IA: split daily-operational pages ("Operate") from
    // setup/explainer pages ("Set up") so a SOC analyst's daily surface
    // isn't diluted by config/how-it-works pages. Uses the existing
    // `section` sub-header mechanism - Discovery-scoped, no shell change.
    items: [
      { to: '/dspm', label: 'Dashboard', icon: LayoutDashboard, section: 'Operate' },
      { to: '/dspm/scans', label: 'Scans', icon: Radar, section: 'Operate' },
      { to: '/dspm/findings', label: 'Findings', icon: AlertTriangle, section: 'Operate' },
      { to: '/dspm/inventory', label: 'Asset Inventory', icon: Database, section: 'Operate' },
      { to: '/dspm/time-travel', label: 'Posture history', icon: History, section: 'Operate' },
      { to: '/dspm/lineage', label: 'Data Lineage', icon: GitBranch, section: 'Operate' },
      { to: '/dspm/section-9', label: "Children's data (Sec 9)", icon: FileSearch, badge: 'India', section: 'Operate' },
      { to: '/dspm/reverse-dsar', label: "Find a person's data", icon: Search, section: 'Operate' },
      // Honey-PII Canaries is intentionally hidden from the default sidebar
      // (Phase 0 Day 5 - 2026-05-22). The page remains reachable via direct
      // URL `/discovery/honey-pii` for tenants experimenting with deception
      // canaries, but it's not a Day-1 BFSI/the customer customer surface.
      { to: '/dspm/connectors', label: 'Connectors', icon: Plug, section: 'Set up' },
      // "Deploy a scanner" is NOT a separate nav line (founder 2026-07-11):
      // it lives INSIDE the Scan targets page as a CTA to the provisioning
      // wizard, so the scan-setup flow is one place, not two.
      { to: '/dspm/target-builder', label: 'Scan targets', icon: Radar, section: 'Set up' },
      { to: '/dspm/bootstrap', label: 'Setup checklist', icon: ListChecks, badge: 'L1-L8', section: 'Set up' },
      { to: '/dspm/classifier', label: 'Classification engine', icon: Cpu, badge: '5x', section: 'Set up' },
      { to: '/dspm/threat-signatures', label: 'Threat signatures', icon: ShieldAlert, badge: 'Malware', section: 'Set up' },
      { to: '/dspm/edm', label: 'Exact-match (EDM)', icon: Fingerprint, badge: 'PII', section: 'Set up' },
      { to: '/dspm/no-db-access', label: 'Scan without credentials', icon: ShieldOff, badge: 'BFSI', section: 'Set up' },
    ],
  },
  {
    id: 'activity',
    label: 'Activity',
    icon: Activity,
    accent: 'from-chart-2 to-chart-7',
    defaultHref: '/activity',
    requiresAnyModule: ['activity'],
    items: [
      { to: '/activity', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/activity/sources', label: 'DAM Sources', icon: Database },
      { to: '/activity/stream', label: 'Live Query Stream', icon: Stream },
      { to: '/activity/findings', label: 'Live Findings', icon: TrendingUp, badge: 'Live' },
      { to: '/activity/ueba', label: 'UEBA Baselines', icon: TrendingUp },
      { to: '/activity/sonar', label: 'Submarine Sonar', icon: Waves },
      { to: '/activity/p-wave', label: 'P-Wave Indicators', icon: Radio },
      // 2026-05-29 - surface the cross-engine kill-chain (compound correlator) + the
      // forensic timeline page that existed but was missing from nav (undiscoverable).
      { to: '/activity/kill-chain', label: 'Kill-Chain Findings', icon: Zap, badge: 'Fusion' },
      { to: '/activity/timeline', label: 'Forensic Timeline', icon: History },
      { to: '/activity/breach-readiness', label: 'Breach Readiness', icon: Timer, badge: 'RBI 6h' },
    ],
  },
  {
    id: 'detection',
    label: 'Detection',
    icon: ShieldCheck,
    accent: 'from-chart-7 to-chart-1',
    defaultHref: '/detection',
    requiresAnyModule: ['ddr'],
    items: [
      { to: '/detection', label: 'Operator Dashboard', icon: LayoutDashboard },
      { to: '/detection/decisions', label: 'Decisions', icon: Gavel },
      { to: '/detection/rules', label: 'Rules', icon: ScrollText },
      { to: '/detection/playbooks', label: 'Playbooks', icon: Workflow },
      { to: '/detection/break-glass', label: 'Break-Glass', icon: KeyRound, badge: '2P' },
      { to: '/detection/siem-gallery', label: 'SIEM / SOAR', icon: Grid3x3, badge: '29' },
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance',
    icon: ShieldCheck,
    accent: 'from-chart-1 to-chart-7',
    defaultHref: '/compliance',
    requiresAnyModule: ['compliance', 'core'],
    items: [
      // P1-9 (2026-05-26) - 14 flat compliance items grouped into 4
      // sub-sections so an evaluator can scan the surface at a glance:
      //   • Overview         - Compliance Center, Regulatory Coverage
      //   • Subject rights   - Notices, Erasure, Rectification,
      //                        Restriction, Portability, Objection
      //                        (the GDPR/DPDPA articles for individual
      //                        data subjects)
      //   • Risk & sensitivity - DPIA, Sensitive-PI tiers, Service
      //                          providers (Art 28)
      //   • Regional + incidents - CCPA opt-outs, SDF (DPDPA §10),
      //                            Breach dispatch
      { to: '/compliance', label: 'Compliance Center', icon: ShieldCheck, section: 'Overview' },
      { to: '/compliance/regulatory-coverage', label: 'Regulatory Coverage', icon: Award, section: 'Overview' },
      { to: '/compliance/notices', label: 'Notices', icon: FileLock2, section: 'Subject rights' },
      { to: '/compliance/erasure', label: 'Erasure (Art 17)', icon: ShieldOff, section: 'Subject rights' },
      { to: '/compliance/rectification', label: 'Rectification (Art 16)', icon: Edit3, section: 'Subject rights' },
      { to: '/compliance/restriction', label: 'Restriction (Art 18)', icon: Pause, section: 'Subject rights' },
      // P1-8 (2026-05-26) - these 7 features shipped weeks ago (each
      // page exists at apps/web-shell/src/pages/compliance/{name}.tsx).
      // The 'new' badge was reused without expiry and is now stale
      // clutter for an evaluator. Removed wholesale; if any of these
      // ever materially changes, the badge can be added back with a
      // dated expiry.
      { to: '/compliance/portability', label: 'Portability (Art 20)', icon: Download, section: 'Subject rights' },
      { to: '/compliance/objection', label: 'Objection (Art 21)', icon: Megaphone, section: 'Subject rights' },
      { to: '/compliance/dpia', label: 'DPIA (Art 35)', icon: ClipboardCheck, section: 'Risk & sensitivity' },
      { to: '/compliance/sensitive-pi', label: 'Sensitive-PI tiers', icon: Lock, section: 'Risk & sensitivity' },
      { to: '/compliance/service-providers', label: 'Service providers (Art 28)', icon: Building2, section: 'Risk & sensitivity' },
      { to: '/compliance/ccpa', label: 'CCPA opt-outs (§1798.120)', icon: ShieldOff, section: 'Regional + incidents' },
      { to: '/compliance/sdf', label: 'SDF (DPDPA §10)', icon: Award, section: 'Regional + incidents' },
      { to: '/compliance/breaches', label: 'Breach dispatch (Art 33)', icon: AlertTriangle, section: 'Regional + incidents' },
    ],
  },
  // Frontier (Sovereign-tier) group - gated behind `frontier` entitlement
  // which no production tenant has by default today. Routes still exist
  // for direct-URL access by curious operators / sales demos; the nav
  // only renders for tenants explicitly entitled to Frontier features.
  // Per Phase 0 audit (docs/master-plan/19-POST-PHASE-0-AUDIT-REPORT.md):
  // these pages are D-grade placeholders, hidden from default nav until
  // Phase 4 builds them properly. Sovereign-tier customers get the flag.
  {
    id: 'frontier',
    label: 'Frontier',
    icon: Sparkles,
    accent: 'from-chart-2 to-chart-7',
    defaultHref: '/frontier',
    requiresAnyModule: ['frontier'],
    items: [
      { to: '/frontier', label: 'Overview', icon: LayoutDashboard },
      { to: '/frontier/federated', label: 'Federated Learning', icon: Brain },
      { to: '/frontier/smpc', label: 'SMPC Benchmarks', icon: Network, badge: 'k≥3' },
      { to: '/frontier/pq', label: 'Post-Quantum Vault', icon: Lock },
      { to: '/frontier/sensors', label: 'eBPF / Network Tap', icon: Cpu },
      { to: '/frontier/byo-llm', label: 'BYO-LLM (Tier 5)', icon: Atom, badge: '9' },
      { to: '/frontier/chat', label: 'AI Assistant', icon: MessageSquare },
      { to: '/frontier/replay', label: 'Compliance Replay', icon: History },
    ],
  },
  {
    // BFSI-grade discoverability: operators logging in for the first
    // time MUST be able to find Settings / Integrations from the side
    // nav without knowing a URL. Per Session #34 (2026-05-13) UX gap.
    id: 'admin',
    label: 'Settings & Admin',
    icon: Settings,
    accent: 'from-slate-400 to-slate-600',
    // 2026-05-27 - explicit landing so the pill click doesn't
    // accidentally route to whatever happens to be the first
    // item in the list (which moved over time).
    defaultHref: '/admin/integrations',
    items: [
      // P1-12 (2026-05-26) - 18 flat admin items grouped into 4
      // sub-sections by intent:
      //   • Integrations    - connectors + Core pairing + JWT verifier
      //                       (anything that wires Discovery to other
      //                       systems)
      //   • Identity & access - Business Units, DPO & Grievance,
      //                         Compliance Posture, RBAM Matrix,
      //                         Custom Roles, Purposes (who can do
      //                         what, on whose behalf)
      //   • Policy & catalog - Adapters, Licenses, DAM Retention,
      //                        Industry Packs, Classifier Vocab,
      //                        India Moats (tenant-shaping config)
      //   • Edge & audit    - Edge Fleet, Provision pod, Egress Log
      //                       (operations + tamper-evidence)
      { to: '/admin/integrations', label: 'Integrations', icon: Plug, badge: 'BFSI', section: 'Integrations' },
      // Phase 2 - the configure-once shared data-source registry (Discovery/DAM/DDR all consume).
      { to: '/admin/data-sources', label: 'Data Sources', icon: Database, section: 'Integrations' },
      // Phase 0 - what each module minimally needs (the module-relevance matrix).
      { to: '/admin/module-setup', label: 'Module Setup', icon: ListChecks, section: 'Integrations' },
      { to: '/admin/integrations/foctta-core', label: '→ FOCTTA Privacy Core', icon: ShieldCheck, section: 'Integrations' },
      // Phase A - Per-tenant JWT verifier registry (drives /v1/auth/exchange).
      { to: '/admin/auth/jwt-verifiers', label: 'JWT Verifiers', icon: KeyRound, section: 'Integrations' },
      { to: '/admin/auth/idp', label: 'SSO / IdP (SAML/OIDC)', icon: KeyRound, section: 'Integrations' },
      { to: '/admin/auth/ldap', label: 'LDAP / Active Directory', icon: KeyRound, section: 'Integrations' },
      { to: '/admin/auth/scim', label: 'SCIM Provisioning', icon: UsersIcon, section: 'Integrations' },
      { to: '/admin/notify', label: 'Notifications (Email/SMS)', icon: Megaphone, section: 'Integrations' },
      { to: '/admin/consent-source', label: 'Consent Source', icon: Plug, section: 'Integrations' },
      { to: '/admin/pan-verifier', label: 'PAN Verifier', icon: BadgeCheck, section: 'Integrations' },
      { to: '/admin/qr-verification', label: 'Identity QR', icon: ScanLine, section: 'Integrations' },
      { to: '/admin/business-units', label: 'Business Units', icon: Building2, section: 'Identity & access' },
      { to: '/admin/bu-source', label: 'BU Source (CSV)', icon: Plug, section: 'Identity & access' },
      { to: '/admin/purposes', label: 'Purposes', icon: Workflow, section: 'Identity & access' },
      // T1.5.B (2026-05-24) - DPDPA §8 DPO + §13 grievance officer surfaced from Core.
      { to: '/admin/dpo-grievance', label: 'DPO & Grievance', icon: ShieldCheck, badge: 'DPDPA', section: 'Identity & access' },
      // F (2026-05-25) - Unified compliance posture across all 7 federation entities.
      { to: '/admin/compliance-posture', label: 'Compliance Posture', icon: ShieldCheck, section: 'Identity & access' },
      // Phase 5b items 4 / 6 / 9 / 7 - RBAM matrix · Purposes · Adapters · Licenses.
      { to: '/admin/rbam', label: 'RBAM Matrix', icon: ShieldCheck, section: 'Identity & access' },
      { to: '/admin/custom-roles', label: 'Custom Roles', icon: KeyRound, badge: '2P', section: 'Identity & access' },
      // T1.1 (2026-05-28) - Discovery-native user management. Backend
      // shipped in c409666; UI shipped to close the Discovery Independence
      // Principle gap (CLAUDE.md §14). Standalone customers no longer
      // need a Core deployment to manage their own users.
      { to: '/admin/users', label: 'Users', icon: UsersIcon, section: 'Identity & access' },
      // T1.2 (2026-05-28) - Tenant-facing aggregate health snapshot.
      // Audit chain + scanners + edge fleet + audit-vault + recent
      // errors + compound correlator in one read.
      { to: '/admin/health', label: 'Tenant Health', icon: Server, section: 'Edge & audit' },
      // T1.4 (2026-05-28) - AuditSink config (SIEM webhook / Splunk
      // HEC etc.). Secondary destination; the per-tenant DB audit chain
      // is always the primary persistence.
      { to: '/admin/audit-sink', label: 'Audit Sink (SIEM)', icon: Plug, section: 'Edge & audit' },
      { to: '/admin/adapters', label: 'Adapters', icon: Plug, section: 'Policy & catalog' },
      { to: '/admin/licenses', label: 'Licenses', icon: Award, section: 'Policy & catalog' },
      { to: '/admin/feature-flags', label: 'Feature Flags', icon: Layers, section: 'Policy & catalog' },
      // Phase 5 (2026-05-22) - DAM retention policy. Per-tenant, industry-default-aware.
      { to: '/admin/retention-policy', label: 'DAM Retention', icon: Timer, badge: '7y', section: 'Policy & catalog' },
      // Phase 6 - Industry context for multi-industry tenants.
      { to: '/admin/industry-packs', label: 'Industry Packs', icon: Layers, badge: 'India', section: 'Policy & catalog' },
      { to: '/admin/industry-overview', label: 'Industry Overview', icon: Layers, section: 'Policy & catalog' },
      { to: '/admin/effective-config', label: 'Effective Config', icon: Layers, section: 'Policy & catalog' },
      { to: '/admin/notification-preferences', label: 'Notification preferences', icon: Atom, section: 'Identity & access' },
      { to: '/admin/audit-logs', label: 'Audit & Logs', icon: History, section: 'Edge & audit' },
      { to: '/admin/classifier-vocab', label: 'Classifier Vocab', icon: Hash, section: 'Policy & catalog' },
      { to: '/admin/india-moats', label: 'India Moats', icon: KeyRound, section: 'Policy & catalog' },
      // Phase 7 - Edge Ops (fleet view + provisioning wizard).
      { to: '/admin/edge-fleet', label: 'Edge Fleet', icon: Server, section: 'Edge & audit' },
      { to: '/admin/agent-entitlement', label: 'Agent Entitlement', icon: Server, section: 'Edge & audit' },
      { to: '/admin/edge-provisioning', label: '→ Provision pod', icon: Cpu, section: 'Edge & audit' },
      // Phase 5b item 8 - Hash-chained egress log for CISO tamper-evidence audits.
      { to: '/admin/egress-log', label: 'Egress Log', icon: FileLock2, section: 'Edge & audit' },
      // Phase 0 cleanup (2026-05-26) - removed misleading entries for
      // `/admin/api-keys` and `/admin/audit`. The previous comment claimed
      // these were "shipped surfaces" but verify-before-fix grep on
      // 2026-05-26 confirmed: zero router.tsx entries + zero page files.
      // Clicking either → 404. Re-introduce only when the actual pages
      // land alongside their router entries.
      // 2026-05-27 - Onboarding checklist moved here from a top-
      // level Welcome & Setup pill. Getting-started surface, not
      // daily-ops, so it lives at the end of the admin list.
      { to: '/welcome', label: 'Onboarding checklist', icon: Sparkles, section: 'Edge & audit' },
    ],
  },
];

export function SideNav({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const router = useRouterState();
  const path = router.location.pathname;
  const { hasAnyModule, loading: entLoading } = useEntitlements();
  // P1 OBS-3.alt - live bootstrap-progress badge (e.g. "5/8" instead
  // of the static "L1-L8" descriptor).
  const bootstrapBadge = useBootstrapBadge();

  // Slice 2.4 - filter NAV by entitlements. While loading, render
  // everything (server-side route gates still enforce truth). A
  // tenant that's missing a module's entitlement sees no nav for it.
  // Inject the live bootstrap badge into the 8-Level Bootstrap nav
  // item at render time so the descriptor reflects state.
  const filteredNav = useMemo(() => {
    const navWithBadges = NAV.map((g) => ({
      ...g,
      items: g.items.map((item) =>
        item.to === '/dspm/bootstrap'
          ? { ...item, badge: bootstrapBadge }
          : item,
      ),
    }));
    if (entLoading) return navWithBadges;
    return navWithBadges.filter((g) => !g.requiresAnyModule || hasAnyModule(g.requiresAnyModule));
  }, [entLoading, hasAnyModule, bootstrapBadge]);

  // Map a URL pathname to its owning section. `/welcome` is now an
  // Admin sub-item; every other top-level segment matches a group id
  // directly. Default to 'discovery' so the post-login Home (/)
  // highlights Discovery rather than nothing.
  function groupFromPath(p: string): NavGroup['id'] {
    const top = p.split('/')[1] ?? '';
    if (top === 'welcome') return 'admin';
    const match = NAV.find((g) => g.id === top);
    return match?.id ?? 'discovery';
  }

  const [activeGroup, setActiveGroup] = useState<NavGroup['id']>(() => groupFromPath(path));

  // Keep the highlighted module-switcher pill in sync as the user
  // navigates around (deep links, command palette jumps, etc).
  useEffect(() => {
    setActiveGroup(groupFromPath(path));
  }, [path]);

  return (
    <aside
      className={cn(
        // flex-none locks the aside to its explicit w-16/w-64 width.
        // Without it, the flex parent's default `flex: 0 1 auto` lets
        // flex-basis fall back to content size - so the aside would
        // stay at the horizontal-logo's content width (~256px) even
        // when w-16 is supposedly active. flex-none = flex: 0 0 auto
        // forces the browser to respect width property.
        // overflow-hidden + min-w-0 prevent children from forcing the
        // aside back to content width.
        'glass relative flex flex-col flex-none min-w-0 overflow-hidden transition-[width] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Brand header - Phase 0+ (2026-05-26 fix): brand mark + wordmark
          are now a Link to "/" so clicking the FOCTTA logo from any
          deep route returns to the landing page (guided tour, demo,
          operator walkthrough CTAs). The sidebar-collapse toggle moved
          to a separate chevron button on the right edge, matching the
          Linear/Salesforce pattern of brand-clicks-home vs separate
          collapse affordance. */}
      {/* 2026-05-27 - brand area matches FOCTTA Core's side-nav
          pattern: fixed h-14 (56px) header with h-7 (28px) logo.
          Mark uses height-based sizing (size = pixel height) so the
          wide 5:1 wordmark renders at ~140px wide, leaving room for
          the collapse-toggle chevron on the right. */}
      <div
        className={cn(
          'flex h-14 items-center border-b border-border/40 shrink-0',
          collapsed ? 'justify-center px-0' : 'justify-between px-4',
        )}
      >
        <Link
          to="/"
          className="flex items-center min-w-0 hover:opacity-80 transition-opacity rounded-md"
          aria-label="Go to FOCTTA Home"
        >
          {collapsed ? (
            <Mark size={28} animated />
          ) : (
            <Mark variant="wordmark" size={28} animated />
          )}
        </Link>
        {!collapsed && (
          <button
            onClick={onToggle}
            className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        )}
      </div>
      {collapsed && (
        <button
          onClick={onToggle}
          className="absolute top-2 right-1 z-10 text-muted-foreground hover:text-foreground transition-colors p-1 rounded bg-card/80"
          aria-label="Expand sidebar"
          title="Expand sidebar"
        >
          <PanelLeftOpen className="h-4 w-4" />
        </button>
      )}

      {/* Module switcher - pills with gradient when active */}
      <div className={cn('px-2 py-3 grid gap-1', collapsed ? 'grid-cols-1' : 'grid-cols-2')}>
        <TooltipProvider delayDuration={200}>
          {filteredNav.map((g) => {
            const Icon = g.icon;
            const active = activeGroup === g.id;
            // 2026-05-27 - the section pill is now a Link, not a button.
            // Clicking "Discovery" navigates to /discovery (its dashboard);
            // clicking "Detection" navigates to /detection (the operator
            // dashboard), etc. The previous click-to-expand-only behaviour
            // confused first-time evaluators who expected the pill to BE
            // the section landing.
            const href = g.defaultHref ?? g.items[0]?.to ?? '/';
            return (
              <Tooltip key={g.id}>
                <TooltipTrigger asChild>
                  <Link
                    to={href}
                    onClick={() => setActiveGroup(g.id)}
                    className={cn(
                      'relative flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs font-medium transition-all duration-200',
                      active
                        ? 'text-foreground bg-primary/10 shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/40',
                    )}
                  >
                    {active && (
                      <motion.div
                        layoutId="active-module-bg"
                        className={cn('absolute inset-0 rounded-md bg-gradient-to-br opacity-15', g.accent)}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      />
                    )}
                    <Icon className="h-4 w-4 shrink-0 relative z-10" />
                    {!collapsed && <span className="relative z-10">{g.label}</span>}
                  </Link>
                </TooltipTrigger>
                {collapsed && <TooltipContent side="right">{g.label}</TooltipContent>}
              </Tooltip>
            );
          })}
        </TooltipProvider>
      </div>

      <Separator />

      {/* Featured top-level links - explicit Home + Innovations hub.
          2026-05-27 - Home added per user feedback that "click the
          logo to go home" isn't obvious to first-time operators.
          The brand mark still goes home for users who know the
          convention; this row is the discoverable alternative. */}
      <div className="px-2 py-2 space-y-1">
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/"
                className={cn(
                  'group relative flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-all duration-150 overflow-hidden',
                  path === '/'
                    ? 'bg-gradient-to-r from-primary/20 to-accent/20 text-foreground font-medium shine-on-hover'
                    : 'text-foreground hover:bg-muted/40 shine-on-hover',
                )}
              >
                <Home className="h-4 w-4 shrink-0 text-muted-foreground" />
                {!collapsed && <span className="flex-1 truncate">Home</span>}
              </Link>
            </TooltipTrigger>
            {collapsed && <TooltipContent side="right">Home</TooltipContent>}
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/innovations"
                className={cn(
                  'group relative flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-all duration-150 overflow-hidden',
                  path === '/innovations'
                    ? 'bg-gradient-to-r from-primary/20 to-accent/20 text-foreground font-medium shine-on-hover'
                    : 'text-foreground hover:bg-muted/40 shine-on-hover',
                )}
              >
                <Lightbulb className="h-4 w-4 shrink-0 text-muted-foreground" />
                {!collapsed && (
                  <>
                    <span className="flex-1 truncate">Innovation catalogue</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded glass-lite uppercase tracking-wide text-muted-foreground">ref</span>
                  </>
                )}
              </Link>
            </TooltipTrigger>
            {collapsed && <TooltipContent side="right">Innovation catalogue</TooltipContent>}
          </Tooltip>
        </TooltipProvider>
      </div>

      <Separator />

      {/* Active group's nav items */}
      <ScrollArea className="flex-1 px-2 py-2">
        <nav className="space-y-0.5">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeGroup}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.18 }}
            >
              <TooltipProvider delayDuration={200}>
                {/* P1-9 + P1-12 (2026-05-26) - track section transitions
                    so we can render a small heading row when an item's
                    section differs from the previous item's. Heading
                    rows are hidden when the sidebar is collapsed. */}
                {(() => {
                  const items = (filteredNav.find((g) => g.id === activeGroup) ?? filteredNav[0])!.items;
                  let lastSection: string | undefined;
                  return items.map((item) => {
                    const Icon = item.icon;
                    const active = path === item.to;
                    const showHeader =
                      !collapsed &&
                      typeof item.section === 'string' &&
                      item.section !== lastSection;
                    if (item.section) lastSection = item.section;
                    return (
                      <div key={item.to}>
                        {showHeader && (
                          <div className="px-2.5 pt-3 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium">
                            {item.section}
                          </div>
                        )}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Link
                              to={item.to}
                              className={cn(
                                'group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-all duration-150',
                                active
                                  ? 'bg-primary/15 text-foreground font-medium'
                                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/40',
                              )}
                            >
                              <Icon className="h-4 w-4 shrink-0" />
                              {!collapsed && (
                                <>
                                  <span className="flex-1 truncate">{item.label}</span>
                                  {item.badge && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded glass-lite text-[10px] uppercase tracking-wide">
                                      {item.badge}
                                    </span>
                                  )}
                                </>
                              )}
                            </Link>
                          </TooltipTrigger>
                          {collapsed && <TooltipContent side="right">{item.label}</TooltipContent>}
                        </Tooltip>
                      </div>
                    );
                  });
                })()}
              </TooltipProvider>
            </motion.div>
          </AnimatePresence>
        </nav>
      </ScrollArea>

      {/* P1-14 (2026-05-26) - footer with real build provenance.
          Version = short git SHA baked at build time. Env derived
          from hostname (so a single bundle can serve prod + dev
          tenant subdomains and still self-identify). Last deploy
          = ISO timestamp from vite.config.ts `define`. */}
      <div className="px-3 py-3 border-t border-border/40 text-[10px] text-muted-foreground">
        {!collapsed ? (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span title="Short git SHA of the deployed bundle">
                {__BUILD_GIT_SHA__}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
                {(() => {
                  const host = typeof window !== 'undefined' ? window.location.hostname : '';
                  if (host.endsWith('.foctta.com') || host === 'foctta.com') return 'prod';
                  if (host.includes('localhost') || host.endsWith('.local')) return 'dev';
                  return 'preview';
                })()}
              </span>
            </div>
            <div
              className="text-[9px] text-muted-foreground/70"
              title={`Bundle built at ${__BUILD_TIME_ISO__}`}
            >
              deployed {(() => {
                try {
                  const t = new Date(__BUILD_TIME_ISO__);
                  const now = Date.now();
                  const minutes = Math.round((now - t.getTime()) / 60_000);
                  if (minutes < 1) return 'just now';
                  if (minutes < 60) return `${minutes}m ago`;
                  const hours = Math.round(minutes / 60);
                  if (hours < 24) return `${hours}h ago`;
                  const days = Math.round(hours / 24);
                  return `${days}d ago`;
                } catch {
                  return __BUILD_TIME_ISO__.slice(0, 10);
                }
              })()}
            </div>
          </div>
        ) : (
          <span className="block h-1.5 w-1.5 rounded-full bg-success animate-pulse mx-auto" />
        )}
      </div>
    </aside>
  );
}
